import { useRouter } from "next/router";
import { useUserContext } from "../../../../contexts/UserProvider";
import { axiosApiInstance } from "../../../../pages/api/axios";
import { useMoneyHashQueryOnView } from "../../../../hooks/useMoneyHashQueryOnView";
import { type ErrorResponse } from "../../../../hooks/useMoneyHashQuery";

interface UseDatadogQueryOptions {
  chartType:
    | "CAPTURE_REVENUE"
    | "AUTHORIZED_REVENUE"
    | "REFUNDED_REVENUE"
    | "TOTAL_TRANSACTIONS"
    | "TOP_PROVIDERS"
    | "TOP_PAYMENT_METHODS"
    | "ERROR_CODES_COUNTS"
    | "DAILY_ERROR_CODES_COUNTS"
    | "BIN_BRANDS"
    | "BIN_ISSUING_COUNTRIES"
    | "BIN_TOP_ISSUERS";

  // Delay chart request by x milliseconds amount
  delayMs?: number;
}

export function useDatadogQuery<TData>({
  chartType,
  delayMs,
}: UseDatadogQueryOptions) {
  const router = useRouter();
  const { start_date, end_date, provider, account, initiation, organization } =
    router.query;
  const { isMoneyHashUser, organizationId } = useUserContext();

  return useMoneyHashQueryOnView<TData>({
    queryKey: [
      chartType,
      {
        start_date,
        end_date,
        organization,
        organizationId,
        provider,
        account,
        initiation,
        delayMs,
      },
    ],
    queryFn: async ({ signal }) => {
      if (delayMs) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      const today = new Date().toISOString().split("T")[0];
      return axiosApiInstance
        .get("/d/metrics/datadog-analytics-dashboard/", {
          signal,
          params: {
            chart: chartType,
            start_date: start_date || today,
            end_date: end_date || today,
            organization: organization || organizationId,
            provider,
            account: account || undefined,
            initiation,
          },
        })
        .then((res) => res.data);
    },
    enabled: !isMoneyHashUser || (isMoneyHashUser && !!organizationId),
    retry: (failureCount, error: ErrorResponse) => {
      if (
        error?.response?.data?.status?.errors?.[0]?.end_date ===
        "maximum date range is 30 days"
      )
        return false;

      if (failureCount >= 2) return false;

      return true;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
